import React from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'

// Detect environment from hostname
const getSentryEnvironment = () => {
  // Check for custom environment variable first
  if (process.env.REACT_APP_SENTRY_ENV) {
    return process.env.REACT_APP_SENTRY_ENV
  }

  // Detect from hostname
  const hostname = window.location.hostname
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('mdi.local')) {
    // Use git user for local environment if available (set at build time)
    const gitUser = process.env.REACT_APP_GIT_USER
    return gitUser ? `local:${gitUser}` : 'development'
  }

  // Check if it's the test/staging environment (dentope.com)
  if (hostname.includes('dentope.com')) {
    return 'test'
  }

  // Check if it's production (dentolize.com)
  if (hostname.includes('dentolize.com')) {
    return 'production'
  }

  // Fallback to NODE_ENV
  return process.env.NODE_ENV || 'development'
}

Sentry.init({
  // dsn: 'https://adac3a563786431689d211050dd102b8@o1154451.ingest.sentry.io/6235312',
  dsn: 'https://4636f0aa88824ce39384df55ab7b9de5@app.glitchtip.com/19411',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  tracesSampleRate: 0,
  environment: getSentryEnvironment(),
  // Release version for source map matching (set at build time)
  release: process.env.REACT_APP_RELEASE || undefined,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  // replaysSessionSampleRate: 0.1,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  // replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    'top.GLOBALS',
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    'fb_xd_fragment',
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    'conduitPage',
    'Not Authorised!',
    'Not Authorised',
    'Not Logged in',
    'NetworkError when attempting to fetch resource',
    'Failed to fetch',
    'Loading chunk'
  ]
})

// Test function to verify Sentry is working - call from browser console: window.testSentry()
window.testSentry = () => {
  const testError = new Error('Sentry Test Error - This is a test to verify error reporting is working')
  testError.name = 'SentryTestError'
  Sentry.captureException(testError)
  console.log('Test error sent to Sentry. Environment:', getSentryEnvironment())
  return 'Test error sent! Check Sentry dashboard.'
}

const container = document.getElementById('root')
const root = createRoot(container)
root.render(<App />)

serviceWorkerRegistration.register()
